import { GenericService } from 'services/genericService';
import {
	assignNumbersForCorpUserRestPost,
	getCreditLimit,
	changeCreditLimit,
	getNumberUsage,
	getUsage,
	getNumbersForUser,
	getAccountNumberInfo,
	detachNumbersFromUser,
	getCpnAccount,
	getBillAccountNoForNumber,
	activateCpn,
	deactivateCpn,
	addShortCode,
	removeShortCode,
	changeShortCode,
	changeAdslPassword,
	sendResetAdslPasswordOtp,
	confirmAdslResetPasswordOtp,
	getFbbEmails,
	addFbbEmail,
	modifyFbbEmailPassword,
	activateStaticIp,
	detachNumberFromProfile,
	getStaticIPOrderStatus,
} from 'utils/api/queries/omantelApi';
import { Observable } from 'rxjs';
import { UsageHistory } from 'utils/api/models/numbers/usageHistory';
import { CreditLimit } from 'utils/api/models/numbers/creditLimit';
import { NumbersForUser } from 'utils/api/models/numbers/numbersForUser';
import { Usage } from 'utils/api/models/numbers/usage';
import { CpnAccount } from 'utils/api/models/numbers/cpnAccount';
import { ProductOrderResponse } from 'utils/api/models/numbers/productOrderResponse';
import { BannedNumbers } from 'utils/api/models/numbers/bannedNumbers';
import { Countries } from 'utils/api/models/numbers/countries';
import { DialingCodes } from 'utils/api/models/numbers/dialingCodes';
import * as payloads from 'utils/api/payloads/omantelApi';
import invalidateCache from 'utils/api/invalidateCache';
import { finalize } from 'rxjs/operators';
import { BillingAccountNoForNumber } from 'utils/api/models/numbers/billingAccountNoForNumber';
import {
	getBannedNumbers,
	getInternationalMinutesCountries,
	getInternationalMinutesDialingCodes,
} from 'utils/api/queries/wcm';
import { CreateProductOrderResponse } from 'utils/api/models/products/createProductOrderResponse';
import { GetFbbEmailsResponse } from 'utils/api/models/numbers/getFbbEmailsResponse';
import {
	getErrorMessages,
	getCommitmentsWithBuiltInInternationalAndRoaming,
	getTariffPlansWithBuiltInInternationalAndRoaming,
} from 'utils/api/queries/wcm';
import { ErrorMessages } from 'utils/api/models/errors/errorMessages';
import { DataUsages } from 'utils/api/models/numbers/dataUsages';
import { getDataUsage } from 'utils/api/queries/microApi';

export class NumbersService extends GenericService {
	public static assignNumbersForCorpUserRestPost(userNumbers: payloads.UserNumbers): Observable<any> {
		return assignNumbersForCorpUserRestPost(userNumbers)
			.withErrorHandling()
			.call()
			.pipe(finalize(() => this.invalidateNumberCache()));
	}

	public static getCreditLimit(accountInfo: payloads.AccountInfo): Observable<CreditLimit> {
		return getCreditLimit(accountInfo)
			.withErrorHandling()
			.call();
	}

	public static changeCreditLimit(creditLimitInfo: payloads.CreditLimitInfo): Observable<any> {
		return changeCreditLimit(creditLimitInfo)
			.disableCache()
			.call();
	}

	public static getUsageHistory(accountInfo: payloads.AccountInfo): Observable<UsageHistory> {
		return getNumberUsage(accountInfo)
			.withErrorHandling()
			.call();
	}

	public static getUsage(accountInfo: payloads.AccountInfo): Observable<Usage> {
		return getUsage(accountInfo)
			.withErrorHandling()
			.call();
	}

	public static getDataUsage(accountNumber: string): Observable<DataUsages> {
		return getDataUsage(accountNumber)
			.withErrorHandling()
			.call();
	}

	public static getNumbersForUser(userId: string): Observable<NumbersForUser> {
		return getNumbersForUser(userId)
			.withErrorHandling()
			.call();
	}

	public static detachNumberFromProfile(masterPhoneNumber: string, phoneNumber: string): Observable<any> {
		return detachNumberFromProfile(masterPhoneNumber, phoneNumber)
			.withErrorHandling()
			.call();
	}

	public static getAccountNumberInfo(accountNumber: string): Observable<any> {
		return getAccountNumberInfo(accountNumber).call();
	}

	public static detachNumbersFromUser(corpUserId: string, numbers: string[]): Observable<any> {
		return detachNumbersFromUser(corpUserId, numbers)
			.withErrorHandling()
			.call()
			.pipe(finalize(() => this.invalidateNumberCache()));
	}

	public static getCpnAccount(billingAccountNumber: string, phoneNumber: string): Observable<CpnAccount> {
		return getCpnAccount(billingAccountNumber, phoneNumber)
			.withErrorHandling()
			.call();
	}

	public static getBillAccountNoForNumber(number: string): Observable<BillingAccountNoForNumber> {
		return getBillAccountNoForNumber(number).call();
	}

	public static activateCpn(
		phoneNumber: string,
		billingId: string,
		productId: string
	): Observable<ProductOrderResponse> {
		return activateCpn(phoneNumber, billingId, productId)
			.disableCache()
			.call();
	}

	public static activateStaticIP(
		phoneNumber: string,
		productId: string,
		staticIpId: string
	): Observable<ProductOrderResponse> {
		return activateStaticIp(phoneNumber, productId, staticIpId)
			.disableCache()
			.call();
	}

	public static getStaticIPOrderStatus(phoneNumber: string): Observable<any> {
		return getStaticIPOrderStatus(phoneNumber)
			.disableCache()
			.call();
	}

	public static deactivateCpn(
		phoneNumber: string,
		billingId: string,
		productId: string,
		shortCode?: string
	): Observable<ProductOrderResponse> {
		return deactivateCpn(phoneNumber, billingId, productId, shortCode)
			.disableCache()
			.call();
	}

	public static addShortCode(phoneNumber: string, shortCode: string): Observable<ProductOrderResponse> {
		return addShortCode(phoneNumber, shortCode)
			.disableCache()
			.call();
	}

	public static removeShortCode(phoneNumber: string, shortCode: string): Observable<ProductOrderResponse> {
		return removeShortCode(phoneNumber, shortCode)
			.disableCache()
			.call();
	}

	public static changeShortCode(
		phoneNumber: string,
		newShortCode: string,
		oldShortCode: string
	): Observable<ProductOrderResponse> {
		return changeShortCode(phoneNumber, newShortCode, oldShortCode)
			.disableCache()
			.call();
	}

	public static getBannedNumbers(): Observable<BannedNumbers> {
		return getBannedNumbers()
			.disableCache()
			.call();
	}

	public static getInternationalMinutesDialingCodes(): Observable<DialingCodes> {
		return getInternationalMinutesDialingCodes()
			.disableCache()
			.call();
	}

	public static getInternationalMinutesCountries(): Observable<Countries> {
		return getInternationalMinutesCountries()
			.disableCache()
			.call();
	}

	public static changeAdslPassword(
		oldPassword: string,
		newPassword: string,
		adslLogin: string
	): Observable<CreateProductOrderResponse> {
		return changeAdslPassword(oldPassword, newPassword, adslLogin)
			.disableCache()
			.call();
	}

	public static sendResetAdslPasswordOtp(adslLogin: string, msisdn: string): Observable<any> {
		return sendResetAdslPasswordOtp(adslLogin, msisdn)
			.disableCache()
			.call();
	}

	public static confirmAdslResetPasswordOtp(
		newPassword: string,
		token: string,
		adslLogin: string,
		msisdn: string
	): Observable<any> {
		return confirmAdslResetPasswordOtp(newPassword, token, adslLogin, msisdn)
			.disableCache()
			.call();
	}

	public static getFbbEmails(adslLogin: string): Observable<GetFbbEmailsResponse> {
		return getFbbEmails(adslLogin).call();
	}

	public static addFbbEmail(
		emailLogin: string,
		password: string,
		adslLogin: string
	): Observable<CreateProductOrderResponse> {
		return addFbbEmail(emailLogin, password, adslLogin).call();
	}

	public static modifyFbbEmailPassword(password: string, email: string): Observable<CreateProductOrderResponse> {
		return modifyFbbEmailPassword(password, email).call();
	}

	public static getErrorMessages(): Observable<ErrorMessages> {
		return getErrorMessages().call();
	}

	private static invalidateNumberCache() {
		invalidateCache(
			[
				// users
				'getCorpUserDetails',
				// organizations
				'getDepartmentDetails',
				// numbers
				'getCorpUserNumbersWithChilds',
				'getNumbersForUser',
				'getNumbersForAccount',
				// active services
				'getNumbersForBillingAccount',
			],
			true
		);
	}

	public static getCommitmentsWithBuiltInInternationalAndRoaming(): Observable<any> {
		return getCommitmentsWithBuiltInInternationalAndRoaming().call();
	}

	public static getTariffPlansWithBuiltInInternationalAndRoaming(): Observable<any> {
		return getTariffPlansWithBuiltInInternationalAndRoaming().call();
	}
}
